/**
 * Relay sites (plan §5 R4; spec §4.1): one ring per link, near the system's edge, in the direction of the system it
 * links to on the galaxy map. Directions are spread apart so rings never crowd each other.
 */
import { range, type Rng } from '../../core/rng'
import type { Galaxy, StarSystem } from '../types'
import type { ReachSystemsConfig } from './config'
import { galaxyToWorld, normalize, quatLookNegZ, scale, shellGap } from './frame'
import type { BodySite, RelaySite, Vec3T } from './types'

const TWO_PI = Math.PI * 2
const RELAX_PASSES = 200
/** Angular slack (rad) so relaxed gaps land on or just above the target, never a round-off below it. */
const RELAX_EPS = 1e-9

interface GalaxyIndex {
  systems: Map<string, StarSystem>
  relays: Map<string, number>
}

const indexCache = new WeakMap<Galaxy, GalaxyIndex>()

function indexOf(galaxy: Galaxy): GalaxyIndex {
  let idx = indexCache.get(galaxy)
  if (idx === undefined) {
    idx = { systems: new Map(galaxy.systems.map((s) => [s.id, s])), relays: new Map(galaxy.relays.map((r, i) => [r.id, i])) }
    indexCache.set(galaxy, idx)
  }
  return idx
}

function wrap(a: number): number {
  const r = a % TWO_PI
  return r < 0 ? r + TWO_PI : r
}

/**
 * Spreads angles (radians) so neighbours around the circle are at least `minSep` apart, by iterative relaxation
 * (each too-close pair is pushed apart symmetrically). The target drops to 360°/n when that is smaller. Returns new
 * angles in [0, 2π), in the input order.
 */
export function spreadAngles(angles: readonly number[], minSep: number): number[] {
  const n = angles.length
  const out = angles.map(wrap)
  if (n < 2) return out
  const sep = Math.min(minSep, TWO_PI / n)
  // Sort once (ties by input index); the relaxation keeps the circular order.
  const order = out.map((_, i) => i).sort((a, b) => out[a] - out[b] || a - b)
  const sorted = order.map((i) => out[i])
  for (let pass = 0; pass < RELAX_PASSES; pass++) {
    let moved = false
    for (let k = 0; k < n; k++) {
      const next = (k + 1) % n
      const gap = next === 0 ? sorted[0] + TWO_PI - sorted[k] : sorted[next] - sorted[k]
      if (gap >= sep - RELAX_EPS) continue
      const push = (sep - gap) / 2 + RELAX_EPS
      sorted[k] -= push
      sorted[next] += push
      moved = true
    }
    if (!moved) break
  }
  const result = new Array<number>(n)
  for (let k = 0; k < n; k++) result[order[k]] = wrap(sorted[k])
  return result
}

/**
 * The position at orbit radius `r`, height `y` and `angle`, moved along that orbit (alternately ahead and behind, in
 * `shellStepDeg` steps, up to half a turn) until it sits `shellClearanceM` outside every body's mass-lock shell. No RNG
 * draws, so every other roll is unchanged; the first clear step wins, so it is deterministic.
 */
function clearOfShells(r: number, y: number, angle: number, bodies: readonly Pick<BodySite, 'position' | 'shellM'>[], cfg: ReachSystemsConfig): Vec3T {
  const at = (a: number): Vec3T => [r * Math.cos(a), y, r * Math.sin(a)]
  const step = (cfg.relays.shellStepDeg * Math.PI) / 180
  const tries = Math.ceil(Math.PI / step)
  for (let k = 0; k <= tries; k++) {
    for (const sign of k === 0 ? [1] : [1, -1]) {
      const p = at(angle + sign * k * step)
      if (shellGap(p, bodies) >= cfg.relays.shellClearanceM) return p
    }
  }
  throw new Error('layout: no relay spot on its orbit clears the body shells')
}

/**
 * One relay site per `system.relayIds` entry, in that order. A site inside (or within `relays.shellClearanceM` of) a
 * body's mass-lock shell is moved along its orbit until it clears (`clearOfShells`).
 */
export function placeRelays(
  galaxy: Galaxy,
  system: StarSystem,
  cfg: ReachSystemsConfig,
  systemRadiusM: number,
  rng: Rng,
  bodies: readonly Pick<BodySite, 'position' | 'shellM'>[] = [],
): RelaySite[] {
  const idx = indexOf(galaxy)
  const links = system.relayIds.map((relayId) => {
    const index = idx.relays.get(relayId)
    if (index === undefined) throw new Error(`layout: ${system.id} lists unknown relay ${relayId}`)
    const relay = galaxy.relays[index]
    const otherSystemId = relay.a === system.id ? relay.b : relay.a
    const other = idx.systems.get(otherSystemId)
    if (other === undefined) throw new Error(`layout: relay ${relayId} links unknown system ${otherSystemId}`)
    const d = galaxyToWorld([other.mapPos[0] - system.mapPos[0], other.mapPos[1] - system.mapPos[1], other.mapPos[2] - system.mapPos[2]])
    return { relayId, index, otherSystemId, long: relay.long, angle: Math.atan2(d[2], d[0]) }
  })
  const angles = spreadAngles(
    links.map((l) => l.angle),
    (cfg.relays.minSeparationDeg * Math.PI) / 180,
  )
  return links.map((l, i): RelaySite => {
    const r = systemRadiusM * range(rng, cfg.relays.orbitFraction[0], cfg.relays.orbitFraction[1])
    const y = range(rng, -cfg.relays.yJitterM, cfg.relays.yJitterM)
    const position = clearOfShells(r, y, angles[i], bodies, cfg)
    const inward = normalize(scale(position, -1))
    return {
      relayId: l.relayId,
      index: l.index,
      otherSystemId: l.otherSystemId,
      long: l.long,
      position,
      rotation: quatLookNegZ(scale(inward, -1)),
      inward,
      apertureM: cfg.relays.apertureM,
      assetId: 'reach.relay',
    }
  })
}
